import { RotateCcw } from "lucide-react";
import type { Backup } from "@/types";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

interface RestoreDialogProps {
  open: boolean;
  backup: Backup | null;
  onClose: () => void;
  onConfirm: () => void;
}

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes === 0) return "—";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function RestoreDialog({
  open,
  backup,
  onClose,
  onConfirm,
}: RestoreDialogProps) {
  if (!backup) return null;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Restore from Backup"
      variant="warning"
      confirmLabel="Restore"
      message={
        <div className="space-y-4">
          <p>
            You are about to restore VM{" "}
            <strong className="text-gray-100">#{backup.vm_id}</strong> from a
            backup.
          </p>

          <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Backup Date</span>
                <p className="text-gray-200">
                  {new Date(backup.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Type</span>
                <p className="capitalize text-gray-200">{backup.type}</p>
              </div>
              <div>
                <span className="text-gray-500">Size</span>
                <p className="text-gray-200">
                  {formatBytes(backup.size_bytes)}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Retention</span>
                <p className="text-gray-200">{backup.retention_days} days</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-yellow-600/30 bg-yellow-600/10 p-3">
            <RotateCcw className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-400" />
            <p className="text-xs text-yellow-300">
              This will overwrite the current VM state. Any data created after
              the backup was taken will be lost. Make sure the VM is stopped
              before restoring.
            </p>
          </div>
        </div>
      }
    />
  );
}
